/**
 * Renderer-dispatch body for the file-preview module.
 *
 * Looks up the right renderer in the registry for the given source and
 * mounts it inside an error boundary. Lives separate from the dialog
 * chrome so a future inline / side-panel variant can reuse it without
 * dragging the modal frame along.
 *
 * The error boundary is the safety net for renderer crashes — a broken
 * file shouldn't take down the entire dialog, just the body. Without
 * it, throwing inside `useMemo` (e.g. malformed slide markup) would
 * unmount up to the chat root.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";

import { pickRenderer } from "../renderers/registry";
import type { FileSource } from "../types";

interface ErrorBoundaryState {
  error: Error | null;
}

class RendererErrorBoundary extends Component<
  { children: ReactNode },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    /**
     * Browser-console log only. The chat surfaces around this don't have
     * a structured client-side logger today (other chat components use
     * the same `console.error` convention for unhandled exceptions —
     * see `share-dialog.tsx`, `chat-history-list.tsx`).
     */
    console.error("[file-preview] renderer crashed", error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
          <p>Couldn&apos;t render this file.</p>
          <p className="text-xs opacity-70">{this.state.error.message}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

export function FilePreviewBody({ source }: { source: FileSource }) {
  const renderer = pickRenderer(source);
  const Renderer = renderer.Component;
  return (
    <RendererErrorBoundary>
      <Renderer source={source} />
    </RendererErrorBoundary>
  );
}
